import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { AnimatedSection } from '../AnimatedSection';

// Review interface
interface Review {
  id: number | string;
  author: string;
  rating: number;
  text: string;
  date: string;
  initials: string;
  profile_photo_url?: string;
}

// Real Google reviews - manually updated
const mockReviews: Review[] = [
  {
    id: 1,
    author: "Psalms Lyra Rojas",
    rating: 5,
    text: "We hired a content creator instead of a videographer because we didn't feel like we needed the whole thing captured in long form film. Frances delivered so many beautiful glimpses of our wedding day, including the ones we named most important to us. She was detailed and thorough during the consultation and follow up calls, all the way to content delivery. We knew we couldn't afford a videographer and it wasn't our priority but content creator was a happy addition and now we do have videos of our wedding day!",
    date: "3 weeks ago",
    initials: "PR"
  },
  {
    id: 2,
    author: "Dalal Hillou",
    rating: 5,
    text: "You are so hard to know what is true when you are planning an event. I can truly say hiring Date Saver Co's content creation services for my wedding was one of the best decisions that I made for my special day. Frances is so great at communication, professionalism, creativity, and making sure that you are comfortable...I would hire her again in a heartbeat. 100% recommend.",
    date: "2 months ago",
    initials: "DH"
  },
  {
    id: 3,
    author: "Diana L.",
    rating: 5,
    text: "GUYS, HACK: book a content creator for your wedding!! Booking Frances as our content creator was the best thing we did for our wedding! From the first call, we knew she had great energy! Frances was easily hands down our favourite vendor. She was non intrusive and worked seamlessly with our photography team. It was truly a blessing to have her be part of our day because she was literally my wedding party. Frances really went above & beyond all our expectations, and played such an invaluable part of our day. We already felt this way, but then to see all the videos & photos she took, she totally blew it out of the water!! I am in awe at how she was able to get such great footage of us especially since she blended so seamlessly in with the guests. Thank you for capturing all the little details that we can revisit years from now!",
    date: "2 months ago",
    initials: "DL"
  }
];

// Star rating component
const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-5 h-5 ${star <= rating ? 'text-amber-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

// Using manual reviews - no API calls needed

// Homepage Carousel Version
export const ReviewsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const reviews = mockReviews; // Using manual reviews directly

  const nextReview = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const currentReview = reviews[currentIndex];

  return (
    <div className="py-16 px-4 md:py-20 bg-[#231f20] overflow-x-hidden">
      <AnimatedSection>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl text-[#f4f1e9] font-the-seasons mb-4 text-center font-normal">
            What Our Clients Say
          </h2>
          <p className="text-[#f4f1e9]/70 text-center mb-12 font-columbia-serial">
            Real experiences from real clients
          </p>

          <div className="relative px-4 md:px-0">
            {/* Review Card */}
            <div className="bg-[#f4f1e9] rounded-3xl p-8 md:p-12 shadow-2xl">
              <div className="flex flex-col items-center text-center">
                {/* Profile Photo or Initial Badge */}
                {currentReview.profile_photo_url ? (
                  <img 
                    src={currentReview.profile_photo_url} 
                    alt={currentReview.author}
                    className="w-16 h-16 rounded-full mb-4 object-cover border-2 border-amber-400"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center mb-4">
                    <span className="text-white font-the-seasons text-2xl font-bold">
                      {currentReview.initials}
                    </span>
                  </div>
                )}

                {/* Author Name */}
                <h3 className="text-xl font-bold font-the-seasons text-[#231f20] mb-2">
                  {currentReview.author}
                </h3>

                {/* Star Rating */}
                <div className="mb-4">
                  <StarRating rating={currentReview.rating} />
                </div>

                {/* Review Text */}
                <p className="text-[#231f20] font-columbia-serial text-base md:text-lg leading-relaxed mb-4 max-w-2xl">
                  "{currentReview.text}"
                </p>

                {/* Date */}
                <p className="text-[#231f20]/60 text-sm font-columbia-serial">
                  {currentReview.date}
                </p>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevReview}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-16 bg-[#f4f1e9] hover:bg-amber-400 text-[#231f20] rounded-full p-3 shadow-lg transition-all duration-300"
              aria-label="Previous review"
            >
              <FiChevronLeft size={24} />
            </button>
            <button
              onClick={nextReview}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-16 bg-[#f4f1e9] hover:bg-amber-400 text-[#231f20] rounded-full p-3 shadow-lg transition-all duration-300"
              aria-label="Next review"
            >
              <FiChevronRight size={24} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-8 bg-amber-400'
                    : 'w-2 bg-[#f4f1e9]/30 hover:bg-[#f4f1e9]/50'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>

          {/* View on Google Button */}
          <div className="text-center mt-8">
            <a
              href="https://www.google.com/maps/place/Date+Saver+Co/@38.1024196,-81.969824,7z/data=!3m1!4b1!4m6!3m5!1s0xac6a8d75066cfd4f:0x8e56fe4608dcd2f3!8m2!3d38.1319444!4d-79.3308385!16s%2Fg%2F11wfpy5yw2"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-[#231f20] font-the-seasons font-bold text-lg rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              See All Reviews on Google
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <p className="text-[#f4f1e9]/50 text-sm font-columbia-serial mt-4">
              Reviews from Google Business
            </p>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Service Pages Grid Version (Compact)
export const ReviewsGrid = () => {
  const reviews = mockReviews; // Using manual reviews directly
  const displayReviews = reviews.slice(0, 3);
  const [expandedReview, setExpandedReview] = useState<number | null>(null);

  const toggleExpand = (reviewId: number) => {
    setExpandedReview(expandedReview === reviewId ? null : reviewId);
  };

  return (
    <AnimatedSection>
      <div className="py-12 md:py-16">
        <h2 className="text-2xl md:text-4xl text-[#231f20] font-the-seasons mb-8 text-center font-normal">
          Trusted by Our Clients
        </h2>

        {/* Mobile: Horizontal Scroll | Desktop: Grid */}
        <div className="md:max-w-6xl md:mx-auto">
          {/* Mobile Scrollable Container */}
          <div className="md:hidden overflow-x-auto scrollbar-hide px-4 -mx-4">
            <div className="flex gap-4 pb-4" style={{ width: 'max-content' }}>
              {displayReviews.map((review) => {
                const isExpanded = expandedReview === review.id;
                const isLongReview = review.text.length > 150;
                
                return (
                <div
                  key={review.id}
                  className="bg-white border border-[#231f20]/10 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer"
                  style={{ width: '280px', minWidth: '280px' }}
                  onClick={() => isLongReview && toggleExpand(Number(review.id))}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-4">
                    {review.profile_photo_url ? (
                      <img 
                        src={review.profile_photo_url} 
                        alt={review.author}
                        className="w-12 h-12 rounded-full object-cover border-2 border-amber-400 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-the-seasons text-lg font-bold">
                          {review.initials}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold font-the-seasons text-[#231f20] truncate">
                        {review.author}
                      </h3>
                      <StarRating rating={review.rating} />
                    </div>
                  </div>

                  {/* Review Text */}
                  <p className={`text-[#231f20] font-columbia-serial text-sm leading-relaxed mb-3 ${isExpanded ? '' : 'line-clamp-4'}`}>
                    "{review.text}"
                  </p>

                  {/* Tap to expand hint */}
                  {isLongReview && !isExpanded && (
                    <p className="text-amber-500 text-xs font-columbia-serial mb-2 italic">
                      Tap to read more...
                    </p>
                  )}

                  {/* Date */}
                  <p className="text-[#231f20]/50 text-xs font-columbia-serial">
                    {review.date}
                  </p>
                </div>
                );
              })}
            </div>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-3 gap-6">
            {displayReviews.map((review) => {
              const isExpanded = expandedReview === review.id;
              const isLongReview = review.text.length > 150;
              
              return (
              <div
                key={review.id}
                className="bg-white border border-[#231f20]/10 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer"
                onClick={() => isLongReview && toggleExpand(Number(review.id))}
              >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                {review.profile_photo_url ? (
                  <img 
                    src={review.profile_photo_url} 
                    alt={review.author}
                    className="w-12 h-12 rounded-full object-cover border-2 border-amber-400 flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-the-seasons text-lg font-bold">
                      {review.initials}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold font-the-seasons text-[#231f20] truncate">
                    {review.author}
                  </h3>
                  <StarRating rating={review.rating} />
                </div>
              </div>

              {/* Review Text */}
              <p className={`text-[#231f20] font-columbia-serial text-sm leading-relaxed mb-3 ${isExpanded ? '' : 'line-clamp-4'}`}>
                "{review.text}"
              </p>

              {/* Click to expand hint */}
              {isLongReview && !isExpanded && (
                <p className="text-amber-500 text-xs font-columbia-serial mb-2 italic">
                  Click to read more...
                </p>
              )}

              {/* Date */}
              <p className="text-[#231f20]/50 text-xs font-columbia-serial">
                {review.date}
              </p>
            </div>
              );
            })}
          </div>
        </div>

        {/* View on Google Button */}
        <div className="text-center mt-8">
          <a
            href="https://www.google.com/maps/place/Date+Saver+Co/@38.1024196,-81.969824,7z/data=!3m1!4b1!4m6!3m5!1s0xac6a8d75066cfd4f:0x8e56fe4608dcd2f3!8m2!3d38.1319444!4d-79.3308385!16s%2Fg%2F11wfpy5yw2"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-[#231f20] font-the-seasons font-bold text-base rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            See All Reviews on Google
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-[#231f20]/40 text-xs font-columbia-serial mt-3">
            Reviews from Google Business
          </p>
        </div>
      </div>
    </AnimatedSection>
  );
};
